import React, { useContext, useState } from 'react';

import 'react-phone-input-2/lib/style.css';
import './Dots.css';

import DotsContext from 'context/DotsContext';
import { Spinner } from 'react-bootstrap';
import { dots } from 'assets/images';
import { getAllStoresForHost } from 'Services/storeService';
import { Button, Tooltip } from 'antd';
import HostConnectModal from 'components/Modal/HostDotsConnect';
import { Link } from 'react-router-dom';
import { useEffectOnce } from 'hooks/useEffectOnce';
import { InfoCircleOutlined } from "@ant-design/icons";

/**
 * This component renders the UI for a host to connect with the dots platform.
 *
 * @returns {ReactNode} A React element that renders the UI for a host to connect with the dots platform.
 */

const Dots = () => {
  const [storesLoading, setStoresLoading] = useState(true);
  const [hostConnectModalOpen, setHostConnectModalOpen] = useState(false);
  const [expandedStores, setExpandedStores] = useState({});

  const { setStoreList, storeList, setSalesStoreList } =
    useContext(DotsContext);

  const [currentStore, setCurrentStore] = useState();

  const openHostConnectModal = (store) => {
    setCurrentStore(store);
    setHostConnectModalOpen(true);
  };

  const closeHostConnectModal = () => {
    setCurrentStore(undefined);
    setHostConnectModalOpen(false);
  };

  const toggleCommission = (storeId) => {
    setExpandedStores(prev => ({
      ...prev,
      [storeId]: !prev[storeId]
    }));
  };

  /**
   * This component renders the UI based on the store connection status
   *
   * @return {ReactNode} React node that renders the UI based on the store connection status
   */

  const getStoreStatus = (store) => {
    if (!Object.keys(store?.store_payment_type).length) {
      return (
        <Button
          type="primary"
          className="connect-store-btn"
          onClick={() => {
            openHostConnectModal(store);
          }}
        >
          Connect to Store
        </Button>
      );
    }

    if (store?.store_payment_type?.dots?.is_verified) {
      return (
        <div className="verify-text">
          Verified (
          {`${store?.store_payment_type?.dots?.dial_code}${store?.store_payment_type?.dots?.phone_number}`}
          )
        </div>
      );
    } else {
      return (
        <Link
          to={store?.store_payment_type?.dots?.connected_flow_id}
          target="_blank"
          className="unverify-text"
        >
          Unverified (
          {`${store?.store_payment_type?.dots?.dial_code}${store?.store_payment_type?.dots?.phone_number}`}
          )
        </Link>
      );
    }
  };

  /**
   * This function checks the disability of the dots connect button
   *
   * @return {Boolean}
   */

  const checkDotsButtonDisabled = () => {
    const filterIndex = storeList.findIndex(
      (store) => store?.store_payment_type?.dots?.is_verified === true
    );

    if (filterIndex >= 0) {
      return false;
    }
    return true;
  };

  /**
   * This function returns the tooltip text
   *
   * @return {string} text to be displayed in tooltip
   */

  const getDotsButtonTooltip = () => {
    const filterIndex = storeList.findIndex(
      (store) => store?.store_payment_type?.dots?.is_verified === true
    );

    if (filterIndex >= 0) {
      return '';
    }
    return 'You need to get verified in atleast one store to be able to login on dots';
  };

  /**
   * This component renders the stores list for the host
   *
   * @return {ReactNode} react node that renders the stores list for the host
   */

  const getStoreList = () => {
    if (!storeList.length) {
      return (
        <div>
          You currently have no active approved product from any store. Please
          continue to <Link to="/user/showrooms">add products</Link> in order to
          connect with a store
        </div>
      );
    }
    
    return (
      <>
        <div>
          {storeList?.map((store, index) => {
            const storeId = store?.store_details?.id || index;
            const isExpanded = expandedStores[storeId];
            
            return (
              <div className="store-card" key={storeId}>
                <div className="store-name">
                  {store?.store_details?.store_name}
                </div>
                
                {/* Commission Dropdown Toggle */}
                <button 
                  className="commission-toggle-btn"
                  onClick={() => toggleCommission(storeId)}
                  type="button"
                >
                  <span className="commission-toggle-text">Store Commission Info</span>
                  <svg 
                    className={`commission-toggle-icon ${isExpanded ? 'expanded' : ''}`}
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>

                {/* Collapsible Commission Details */}
                <div className={`commission-collapse ${isExpanded ? 'show' : ''}`}>
                  <div className="commission-details-grid">
                    <div className="commission-detail-item">
                      <div className="commission-detail-icon demo-color">
                        <Tooltip
                          title={
                            "How much the store pays you for each meeting you attend."
                          }
                        >
                          <InfoCircleOutlined />
                        </Tooltip>
                      </div>
                      <div className="commission-detail-content">
                        <div className="commission-detail-label">Video meeting Commission</div>
                        <div className="commission-detail-value">
                          {store?.store_details?.demo_commission_type === "PERCENTAGE" 
                            ? `${store?.store_details?.demo_commission}%` 
                            : `$${store?.store_details?.demo_commission}`}
                        </div>
                      </div>
                    </div>

                    <div className="commission-detail-item">
                      <div className="commission-detail-icon sales-color">
                        <Tooltip
                          title={
                            "How much the store pays you in commission for the sales your meetings generate."
                          }
                        >
                          <InfoCircleOutlined />
                        </Tooltip>
                      </div>
                      <div className="commission-detail-content">
                        <div className="commission-detail-label">Sales Commission</div>
                        <div className="commission-detail-value">
                          {store?.store_details?.demo_sales_commission_type === "PERCENTAGE" 
                            ? `${store?.store_details?.demo_sales_commission}%` 
                            : `$${store?.store_details?.demo_sales_commission}`}
                        </div>
                      </div>
                    </div>

                    <div className="commission-detail-item">
                      <div className="commission-detail-icon noshow-color">
                        <Tooltip
                          title={
                            "How much the store pays you for a meeting if no guest shows up."
                          }
                        >
                          <InfoCircleOutlined />
                        </Tooltip>
                      </div>
                      <div className="commission-detail-content">
                        <div className="commission-detail-label">No Show Commission</div>
                        <div className="commission-detail-value">
                          {store?.store_details?.no_show_demo_commission_type === "PERCENTAGE" 
                            ? `${store?.store_details?.no_show_demo_commission}%` 
                            : `$${store?.store_details?.no_show_demo_commission}`}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="seperator"></div>
                <div className="store-status">{getStoreStatus(store)}</div>
              </div>
            );
          })}
        </div>
        <div className="payouts-text-container">
          <div className="availability-banner">
          <span className="icon">🌎</span>
          <span>Payments available in <strong>United States only</strong></span>
        </div>
          <div className="payouts-text">
            To make edits to your payment methods, go to dots.
          </div>
          <div className="dots-image">
            <img height={32} width={100} src={dots} alt="" />
          </div>
          <div>
            <Tooltip title={getDotsButtonTooltip()}>
              <Button
                type="primary"
                className="go-dots-btn"
                disabled={checkDotsButtonDisabled()}
                onClick={() => {
                  window.open(process.env.REACT_APP_DOTS_URL, '_blank');
                }}
              >
                Go to Dots
              </Button>
            </Tooltip>
          </div>
        </div>
      </>
    );
  };

  /**
   * This function is used to get all the stores
   *
   * @return {void}
   */

  const getAllStores = async () => {
    try {
      const storesList = await getAllStoresForHost();
      setStoreList(storesList?.data?.data);
      setSalesStoreList(storesList?.data?.sales_person_data);
      setStoresLoading(false);
    } catch (err) {
      setStoresLoading(false);
      console.log(err);
    }
  };

  useEffectOnce(() => {
    getAllStores();
  }, []);

  if (storesLoading) {
    return (
      <div className="d-flex justify-content-center">
        <div className="spinner-dots">
          <Spinner className="custom-blink" animation="grow" />{' '}
          <Spinner className="custom-blink" animation="grow" />{' '}
          <Spinner className="custom-blink" animation="grow" />
        </div>
      </div>
    );
  }

  return (
    <div>
      {getStoreList()}
      <HostConnectModal
        hostConnectModalOpen={hostConnectModalOpen}
        handleModalClose={closeHostConnectModal}
        currentStore={currentStore}
      />
    </div>
  );
};

export default Dots;
